import { BotPlayer } from '../ai/BotPlayer.js';
import { CommanderBotPlayer } from '../ai/CommanderBotPlayer.js';
import { TacticalBotPlayer } from '../ai/TacticalBotPlayer.js';
import type { BotConfig, BotDifficulty } from '../ai/difficulty.js';
import { DEFAULT_BOT_DIFFICULTY } from '../ai/difficulty.js';
import type { PlayerId } from '../simulation/ids.js';

import type { CommandSource, CommandSourceIo } from './CommandSource.js';

// Bot CommandSource. The active BotPlayer subclass is selected by difficulty
// (Recruit / Tactical / Commander) — same emit/snapshot pipe in either case
// (Rule 6).
//
// Per docs/07_BOT_AI.md the V1 default is Tactical; Recruit is retained for
// tutorial / accessibility; Commander is the V1.5 top tier.
type ActiveBot =
  | { kind: 'recruit'; bot: BotPlayer }
  | { kind: 'tactical'; bot: TacticalBotPlayer }
  | { kind: 'commander'; bot: CommanderBotPlayer };

export class BotCommandSource implements CommandSource {
  readonly playerId: PlayerId;
  readonly difficulty: BotDifficulty;
  private active: ActiveBot | null = null;

  constructor(playerId: PlayerId, config: BotConfig = { difficulty: DEFAULT_BOT_DIFFICULTY }) {
    this.playerId = playerId;
    this.difficulty = config.difficulty;
  }

  start(io: CommandSourceIo): void {
    if (this.difficulty === 'commander') {
      const bot = new CommanderBotPlayer({ playerId: this.playerId });
      bot.start(io);
      this.active = { kind: 'commander', bot };
    } else if (this.difficulty === 'tactical') {
      const bot = new TacticalBotPlayer({ playerId: this.playerId });
      bot.start(io);
      this.active = { kind: 'tactical', bot };
    } else {
      const bot = new BotPlayer({ playerId: this.playerId });
      bot.start(io);
      this.active = { kind: 'recruit', bot };
    }
  }

  stop(): void {
    this.active?.bot.stop();
    this.active = null;
  }
}
